import { Pencil } from 'lucide-react';
import { TASK_EDITING_IDLE_MS } from '@shared/types/tasks';
import { AppAlert } from '@/components/AppAlert';
import { Button } from '@/components/ui/button';

/**
 * Who else has a task's description open to edit (T-46), above the description:
 * - `editing`: someone else is typing in it; the description won't open for you.
 * - `idle`: they opened it but haven't typed for TASK_EDITING_IDLE_MS; Edit anyway opens it.
 * - `also`: you're in the editor and so are they; whoever saves second is asked to reload.
 * - `own-tab`: your own other tab has it open; a note only.
 */
export type TaskEditingNoticeKind = 'editing' | 'idle' | 'also' | 'own-tab';

export function TaskEditingNotice({
  kind,
  who = 'Someone',
  many = false,
  onEditAnyway,
}: {
  kind: TaskEditingNoticeKind;
  /** Their names: "Ana", "Ana and Ben", "Ana and 2 others". */
  who?: string;
  /** More than one person, for "are" rather than "is". */
  many?: boolean;
  /** `idle` only. */
  onEditAnyway?: () => void;
}) {
  const icon = <Pencil className="size-3.5" />;
  const is = many ? 'are' : 'is';
  if (kind === 'own-tab') {
    return (
      <AppAlert tone="neutral" compact icon={icon}>
        You're editing this description in another tab.
      </AppAlert>
    );
  }
  if (kind === 'also') {
    return (
      <AppAlert tone="warning" compact icon={icon}>
        {who} {is} editing it too. If they save first, yours won't be saved until you reload their version.
      </AppAlert>
    );
  }
  return (
    <AppAlert
      tone="info"
      compact
      icon={icon}
      action={
        kind === 'idle' && onEditAnyway ? (
          <Button type="button" variant="outline" size="xs" onClick={onEditAnyway}>
            Edit anyway
          </Button>
        ) : undefined
      }
    >
      {kind === 'editing'
        ? `${who} ${is} editing the description. You can edit it when they're done.`
        : `${who} opened the description to edit but ${many ? "haven't" : "hasn't"} typed for over ${TASK_EDITING_IDLE_MS / 60_000} minutes.`}
    </AppAlert>
  );
}
